import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessIcon from '@mui/icons-material/Business';
import { useTheme } from '../../context/ThemeContext';

const ContactInfo = () => {
  const [copiedItem, setCopiedItem] = useState(null);
  const { themeMode, jamaicanColors } = useTheme();

  const contactDetails = {
    email: 'support@jamdungjobs.com',
    tagline: 'Building Jamaica\'s Future Workforce'
  };

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(type);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const ContactItem = ({ icon, label, value, type }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        backgroundColor: themeMode === 'light' ? 'rgba(0, 150, 57, 0.05)' : 'rgba(76, 175, 80, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: themeMode === 'light' ? 'rgba(0, 150, 57, 0.1)' : 'rgba(76, 175, 80, 0.15)',
          transform: 'translateX(4px)'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: jamaicanColors.green,
          color: '#ffffff',
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: themeMode === 'light' ? 'text.secondary' : 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontSize: '0.75rem'
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: themeMode === 'light' ? 'text.primary' : 'text.primary',
            wordBreak: 'break-word'
          }}
        >
          {value}
        </Typography>
      </Box>
      <Tooltip title={copiedItem === type ? 'Copied!' : 'Copy to clipboard'}>
        <IconButton
          size="small"
          onClick={() => handleCopy(value, type)}
          sx={{
            color: copiedItem === type ? jamaicanColors.green : 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: jamaicanColors.green,
              transform: 'scale(1.1)'
            }
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Card
      sx={{
        maxWidth: { xs: '100%', sm: 600 },
        margin: 'auto',
        borderRadius: 3,
        boxShadow: themeMode === 'light' 
          ? '0 4px 20px rgba(0,0,0,0.08)' 
          : '0 4px 20px rgba(0,0,0,0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: themeMode === 'light' 
            ? '0 6px 30px rgba(0,0,0,0.12)' 
            : '0 6px 30px rgba(0,0,0,0.4)'
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: jamaicanColors.gold,
              mb: 2
            }}
          >
            <BusinessIcon sx={{ fontSize: 32, color: jamaicanColors.black }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: themeMode === 'light' ? jamaicanColors.darkGreen : jamaicanColors.lightGreen,
              mb: 1
            }}
          >
            Contact JamDung Jobs
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {contactDetails.tagline}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ContactItem
            icon={<EmailIcon />}
            label="Email"
            value={contactDetails.email}
            type="email"
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: themeMode === 'light' 
              ? 'rgba(255, 215, 0, 0.1)' 
              : 'rgba(255, 215, 0, 0.15)',
            border: `1px solid ${jamaicanColors.gold}`
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'text.primary',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            Connecting talented Jamaicans with opportunities across the island.
          </Typography>
        </Box>
      </CardContent>

      <Snackbar
        open={copiedItem !== null}
        autoHideDuration={2000}
        onClose={() => setCopiedItem(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          sx={{
            backgroundColor: jamaicanColors.green,
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff'
            }
          }}
        >
          Email copied to clipboard!
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ContactInfo;
